import type { IAPServiceMethods } from '@forest/native-services'
import { isHTTPError, type KyInstance } from 'ky'
import pRetry from 'p-retry'
import type { Platform } from '~/lib/params'

const PaymentSystemID = {
  PingXX: 7,
  GooglePlay: 8,
  AppStoreStoreKit1: 9,
  AppStoreStoreKit2: 11,
} as const

const paymentSystemIdByPlatform: Record<Platform, number> = {
  china_android: PaymentSystemID.PingXX,
  googleplay_android: PaymentSystemID.GooglePlay,
  ios: PaymentSystemID.AppStoreStoreKit2,
}

interface PostPurchaseResponse {
  results: { purchase: { id: number } }[]
}

export interface PurchaseBoostingPackDeps {
  api: { service: KyInstance; receipt: KyInstance }
  platform: Platform
  userId: number
  product: { product_id: number; sku: string }
  nativePurchase: IAPServiceMethods['purchase']
  closeTransaction: IAPServiceMethods['closeTransaction']
  onStepError?: (error: unknown, args: Record<string, string | number>) => void
  isDev?: boolean
  /** Extra attempts for the opaque iOS `unknown` StoreKit error. Default 2. */
  iosUnknownRetries?: number
}

/**
 * Orchestrates a one-time consumable IAP for a birthday Boosting Pack,
 * mirroring focus-pass's purchase flow: create order → native store purchase →
 * mark-processing → post receipt → close transaction. Pure (deps injected) so
 * it can be unit-tested without the native bridge or a real backend.
 */
export async function purchaseBoostingPack(
  deps: PurchaseBoostingPackDeps,
): Promise<void> {
  const {
    api,
    platform,
    userId,
    product,
    nativePurchase,
    closeTransaction,
    onStepError,
    iosUnknownRetries = 2,
    isDev = import.meta.env.DEV,
  } = deps

  // 1. Create order
  const response = await api.service
    .post(`store/users/${userId}/purchase`, {
      json: {
        product_id: product.product_id,
        iap_sku: product.sku,
        quantity: 1,
      },
    })
    .json<PostPurchaseResponse>()
  const purchaseId = response.results[0].purchase.id

  // In local dev the native store is unavailable, so stop once the order
  // exists — the rest of the flow is exercised through mocks/MSW.
  if (isDev) {
    return
  }

  const postTransaction = async (txId: string): Promise<void> => {
    await api.receipt.post(`projects/6/users/${userId}/pending_iap_receipts`, {
      json: {
        receipt_data: txId,
        sku_id: product.sku,
        payment_system_id: paymentSystemIdByPlatform[platform],
      },
    })
  }

  const executeNativePurchase = async (): Promise<string> => {
    const result = await pRetry(
      () =>
        nativePurchase({
          product_id: product.product_id,
          product_type: 'consumable',
          sku: product.sku,
          receipt_system_account_id: null,
        }).then((r) => {
          // The opaque iOS `unknown` StoreKit error is often transient — retry.
          if (platform === 'ios' && !r.success && r.error === 'unknown') {
            throw new Error('unknown')
          }
          return r
        }),
      { retries: platform === 'ios' ? iosUnknownRetries : 0, minTimeout: 0 },
    ).catch(() => ({ success: false as const, error: 'unknown' as const }))
    if (!result.success) {
      const error = new NativeStoreError(result.error, purchaseId)
      // 'interrupted' is a user cancel — surface it silently (no step report,
      // no dialog), matching focus-pass.
      if (result.error !== 'interrupted') {
        onStepError?.(error, { step: 'native_purchase', purchaseId })
      }
      throw error
    }
    return result.data.transaction_id
  }

  // 2. Native store purchase. If it fails, mark the order failed (best-effort)
  // and rethrow so the caller surfaces the right dialog.
  let transactionId = await executeNativePurchase().catch((err) => {
    void api.service.post(
      `store/users/${userId}/purchase/${purchaseId}/mark-failed`,
    )
    throw err
  })

  // 3. Mark purchase as processing
  try {
    await api.service.post(
      `store/users/${userId}/purchase/${purchaseId}/mark-processing`,
    )
  } catch (error) {
    onStepError?.(error, {
      step: 'mark_processing',
      purchaseId,
      transactionId,
    })
    throw new AfterPaymentError(purchaseId, transactionId, 'mark_processing')
  }

  // 4. Post transaction to the receipt system. A 409 means the receipt was
  // already posted (e.g. by a previous interrupted attempt): close the stale
  // transaction, buy a fresh one from the store, and repost once.
  try {
    await postTransaction(transactionId)
  } catch (error) {
    if (!isConflictError(error)) {
      onStepError?.(error, {
        step: 'post_transaction',
        purchaseId,
        transactionId,
      })
      throw new AfterPaymentError(purchaseId, transactionId, 'post_transaction')
    }
    try {
      await closeTransaction({ transaction_id: transactionId })
    } catch (e) {
      console.error('Failed to close transaction before retrying', e)
    }
    transactionId = await executeNativePurchase()
    try {
      await postTransaction(transactionId)
    } catch (retryError) {
      onStepError?.(retryError, {
        step: 'post_transaction',
        purchaseId,
        transactionId,
      })
      throw new AfterPaymentError(purchaseId, transactionId, 'post_transaction')
    }
  }

  // 5. Tell the native store to close the transaction. The purchase has already
  // succeeded server-side, so a close failure is reported but never rethrown.
  try {
    await closeTransaction({ transaction_id: transactionId })
  } catch (error) {
    onStepError?.(error, {
      step: 'close_transaction',
      purchaseId,
      transactionId,
    })
  }
}

function isConflictError(error: unknown): boolean {
  return isHTTPError(error) && error.response.status === 409
}

type NativeStoreErrorType =
  | 'interrupted'
  | 'pending'
  | 'product not found'
  | 'invalid transaction'
  | 'unknown'

export class NativeStoreError extends Error {
  public purchaseId: number
  constructor(message: NativeStoreErrorType, purchaseId: number) {
    super(message)
    this.name = 'NativeStoreError'
    this.purchaseId = purchaseId
  }
}

export class AfterPaymentError extends Error {
  public purchaseId: number
  public transactionId: string | null
  public node: string
  constructor(purchaseId: number, transactionId: string | null, node: string) {
    super('Purchase failed')
    this.name = 'AfterPaymentError'
    this.purchaseId = purchaseId
    this.transactionId = transactionId
    this.node = node
  }
}
